import { useState, useEffect, useCallback } from "react";
import {
	getTasksForExtra,
	completeTask,
	revertTask,
	getTaskStats,
	getTaskTemplates,
	createTasksForExtra,
	type ExtraEventTask,
	type TaskTemplate,
	type TasksResponse,
	type TaskStatsResponse,
	type TaskCompletionResponse,
} from "../api/tasks.api";
import { toast } from "sonner";
import { ApiError } from "../services/api.service";

/**
 * Hook to fetch tasks for a specific extra and event
 */
export function useTasksForExtra(
	extraId: number,
	eventId: number,
	enabled = true
) {
	const [data, setData] = useState<TasksResponse | null>(null);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);

	const fetchTasks = useCallback(async () => {
		if (!enabled || !extraId || !eventId) return;

		try {
			setIsLoading(true);
			setError(null);
			const response = await getTasksForExtra(extraId, eventId);
			setData(response);
		} catch (err) {
			const errorMessage =
				err instanceof ApiError
					? err.message
					: "Fehler beim Laden der Aufgaben";
			setError(errorMessage);
			setData(null);
		} finally {
			setIsLoading(false);
		}
	}, [extraId, eventId, enabled]);

	useEffect(() => {
		fetchTasks();
	}, [fetchTasks]);

	return {
		data,
		isLoading,
		error,
		refetch: fetchTasks,
	};
}

/**
 * Hook to complete a task
 */
export function useCompleteTask(extraId: number, eventId: number) {
	const [isPending, setIsPending] = useState<boolean>(false);

	const mutateAsync = useCallback(
		async (taskId: number): Promise<TaskCompletionResponse> => {
			try {
				setIsPending(true);
				const response = await completeTask(taskId);

				// Find task name for success message
				const taskName = "Aufgabe"; // We could get this from context if needed
				toast.success(`"${taskName}" wurde erfolgreich abgeschlossen`);

				return response;
			} catch (err) {
				const errorMessage =
					(err as ApiError)?.message || "Fehler beim Abschließen der Aufgabe";
				toast.error(errorMessage);
				throw err;
			} finally {
				setIsPending(false);
			}
		},
		[]
	);

	return {
		mutateAsync,
		isPending,
	};
}

/**
 * Hook to revert a completed task back to pending
 */
export function useRevertTask(extraId: number, eventId: number) {
	const [isPending, setIsPending] = useState<boolean>(false);

	const mutateAsync = useCallback(
		async (taskId: number): Promise<TaskCompletionResponse> => {
			try {
				setIsPending(true);
				const response = await revertTask(taskId);

				toast.success("Aufgabe wurde zurückgesetzt");

				return response;
			} catch (err) {
				const errorMessage =
					(err as ApiError)?.message || "Fehler beim Zurücksetzen der Aufgabe";
				toast.error(errorMessage);
				throw err;
			} finally {
				setIsPending(false);
			}
		},
		[]
	);

	return {
		mutateAsync,
		isPending,
	};
}

/**
 * Hook to fetch task statistics for an event
 */
export function useTaskStats(eventId: number, enabled = true) {
	const [data, setData] = useState<TaskStatsResponse | null>(null);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);

	const fetchStats = useCallback(async () => {
		if (!enabled || !eventId) return;

		try {
			setIsLoading(true);
			setError(null);
			const response = await getTaskStats(eventId);
			setData(response);
		} catch (err) {
			const errorMessage =
				err instanceof ApiError
					? err.message
					: "Fehler beim Laden der Aufgaben-Statistiken";
			setError(errorMessage);
			setData(null);
		} finally {
			setIsLoading(false);
		}
	}, [eventId, enabled]);

	useEffect(() => {
		fetchStats();
	}, [fetchStats]);

	return {
		data,
		isLoading,
		error,
		refetch: fetchStats,
	};
}

/**
 * Hook to fetch task templates (admin only)
 */
export function useTaskTemplates(enabled = false) {
	const [data, setData] = useState<{ templates: TaskTemplate[] } | null>(null);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);

	const fetchTemplates = useCallback(async () => {
		if (!enabled) return;

		try {
			setIsLoading(true);
			setError(null);
			const response = await getTaskTemplates();
			setData(response);
		} catch (err) {
			const errorMessage =
				err instanceof ApiError
					? err.message
					: "Fehler beim Laden der Aufgabenvorlagen";
			setError(errorMessage);
			setData(null);
		} finally {
			setIsLoading(false);
		}
	}, [enabled]);

	useEffect(() => {
		fetchTemplates();
	}, [fetchTemplates]);

	return {
		data,
		isLoading,
		error,
		refetch: fetchTemplates,
	};
}

/**
 * Hook to create tasks for an extra/event (testing/admin purposes)
 */
export function useCreateTasksForExtra() {
	const [isPending, setIsPending] = useState<boolean>(false);

	const mutate = useCallback(
		async ({ extraId, eventId }: { extraId: number; eventId: number }) => {
			try {
				setIsPending(true);
				await createTasksForExtra(extraId, eventId);
				toast.success("Aufgaben wurden erfolgreich erstellt");
			} catch (err) {
				const errorMessage =
					(err as ApiError)?.message || "Fehler beim Erstellen der Aufgaben";
				toast.error(errorMessage);
			} finally {
				setIsPending(false);
			}
		},
		[]
	);

	return {
		mutate,
		isPending,
	};
}

/**
 * Helper hook to group tasks by type and completion status
 */
export function useTaskGroups(extraId: number, eventId: number) {
	const {
		data: tasksData,
		isLoading,
		refetch,
	} = useTasksForExtra(extraId, eventId);

	if (isLoading || !tasksData?.tasks) {
		return { isLoading: true };
	}

	const tasks = tasksData.tasks;

	// Tasks are already sorted correctly by backend following task_template order
	// Passenger-specific tasks appear multiple times (once per passenger) in their template position
	const allSortedTasks = tasks;

	// Group tasks for display sections (optional - for separate rendering)
	const generalTasks = tasks.filter(
		(task: ExtraEventTask) => !task.requires_passenger
	);
	const passengerTasks = tasks.filter(
		(task: ExtraEventTask) => task.requires_passenger
	);

	// Get completion stats
	const totalTasks = tasks.length;
	const completedTasks = tasks.filter(
		(task: ExtraEventTask) => task.status === "completed"
	).length;
	const completionPercentage =
		totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

	return {
		isLoading: false,
		generalTasks,
		passengerTasks,
		allTasks: allSortedTasks,
		totalTasks,
		completedTasks,
		completionPercentage,
		refetch,
	};
}
